// Welle 4.1 — Canonical coverage with the 4-stage traffic light system
// (🟢 Vollständig / 🟡 Gut / 🟠 Verbesserbar / 🔴 Kritisch).
import React from "react";
import { useTranslation } from "react-i18next";
import { Braces } from "lucide-react";
import {
  SectionCard, ProgressBar, coverageStatus, trafficLevel, TrafficLightBadge,
} from "./AdminV2Kit";

export default function CanonicalCoverageCard({ data, developer = false }) {
  const { t } = useTranslation("admin");
  if (!data?.ok) return null;
  const domains = data.domains || [];
  const avg = data.avg_coverage_pct;
  const avgLevel = trafficLevel(avg);

  return (
    <SectionCard
      title={t("modules.canonical.title")}
      icon={Braces}
      status={coverageStatus(avg)}
      testid="adminv2-canonical-card"
      actions={
        <div className="flex items-center gap-3">
          <TrafficLightBadge
            pct={avg}
            label={t(`trafficLight.${avgLevel.key}`)}
            testid="adminv2-canonical-overall-light"
          />
          <div className="text-right">
            <div className="text-2xl font-semibold text-zinc-100 tabular-nums">
              {avg != null ? `${avg} %` : "—"}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
              {t("modules.canonical.avg")} · {data.domains_tracked}{" "}
              {t("modules.canonical.domains")}
            </div>
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
        {domains.map((d) => {
          const dupe = domains.filter((x) => x.domain === d.domain).length > 1;
          const vertical = d.collection?.startsWith("real_estate")
            ? t("vertImmo")
            : t("vertAuto");
          const label = `${t(`modules.canonical.domainNames.${d.domain}`, {
            defaultValue: d.domain,
          })}${dupe ? ` (${vertical})` : ""}`;
          const pct = d.coverage_pct;
          const lvl = trafficLevel(pct);
          return (
            <div key={`${d.collection}:${d.domain}`} data-testid={`adminv2-coverage-${d.domain}`}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="flex items-center gap-1.5 text-zinc-300">
                  <span aria-hidden="true" className="text-[11px] leading-none">
                    {lvl.emoji}
                  </span>
                  {label}
                  {developer && (
                    <span className="text-[10px] text-zinc-600 font-mono">
                      {d.collection}
                    </span>
                  )}
                </span>
                <span className="tabular-nums text-zinc-200 font-medium">
                  <span className="mr-1.5 text-[10px] font-normal text-zinc-500">
                    {t(`trafficLight.${lvl.key}`)}
                  </span>
                  {pct != null ? `${pct} %` : t("modules.canonical.noData")}
                  {developer && d.unknown > 0 && (
                    <span className="ml-1.5 text-[10px] text-rose-300">
                      {d.unknown} {t("modules.canonical.unknown")}
                    </span>
                  )}
                </span>
              </div>
              <ProgressBar pct={pct} />
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}
